/**
 * Minimal `.npy` (NumPy format v1/v2) reader — the inverse, in the browser,
 * of `organism.observatory.artifacts.write_npy_artifact` (`numpy.save`,
 * `allow_pickle=False`). Format reference: NEP 1 / the NumPy `.npy` spec.
 *
 * We only need read support for the small set of dtypes the tap actually
 * writes (`float16`, `float32`, `uint8`) plus a few more for robustness
 * (`float64`, `uint16`, `int32`, `int64`, `uint64`), all little-endian
 * (numpy on x86_64/arm64 writes native little-endian by default, which is
 * what every dtype string this codebase uses — `<f4`, `<f2`, `|u1` —
 * declares). Big-endian variants are handled too wherever the existing
 * `littleEndian` flag (derived from the `>` dtype prefix) already threads
 * through a reader — same pattern as every other dtype here, not something
 * added specially for int64/uint64.
 *
 * `int64`/`uint64` (`<i8`/`<u8`, e.g. `region_flow.py`'s
 * `unbound_pool_neurons`) are read via `DataView.getBigInt64`/
 * `getBigUint64` and then narrowed to `Number` before landing in this
 * module's uniform `Float64Array` output. That narrowing is exact for every
 * value up to 2**53 (`Number.MAX_SAFE_INTEGER`); this codebase's largest
 * plausible int64 payload is a neuron/region/slot count in the low
 * thousands, so the precision loss BigInt->Number could in principle cause
 * above 2**53 never applies here — deliberate, not a silent truncation.
 * `NpyArray.data` stays a plain `Float64Array`, the contract every other
 * integer dtype in this file already follows, rather than introducing a
 * `BigInt64Array`/`BigUint64Array` variant that every downstream consumer
 * (`typedArrays.ts`'s `toUint32`/`toUint16`/etc., `BrainPanel.ts`'s
 * region-flow math) would need its own bigint-aware code path for, just to
 * carry values that always fit safely in a `Number`.
 */

export class NpyParseError extends Error {}

export interface NpyArray {
  readonly shape: readonly number[];
  readonly descr: string;
  readonly fortranOrder: boolean;
  /** Flattened, row-major (C order) numeric data, upcast to float64 so every
   * supported dtype (including float16, which has no native TypedArray) is
   * representable uniformly. Reshape using `shape` at the call site. */
  readonly data: Float64Array;
}

const MAGIC = [0x93, 0x4e, 0x55, 0x4d, 0x50, 0x59]; // "\x93NUMPY"

function float16ToFloat64(half: number): number {
  const sign = (half & 0x8000) !== 0 ? -1 : 1;
  const exponent = (half >> 10) & 0x1f;
  const fraction = half & 0x3ff;
  if (exponent === 0) {
    return sign * 2 ** -14 * (fraction / 1024);
  }
  if (exponent === 0x1f) {
    return fraction ? NaN : sign * Infinity;
  }
  return sign * 2 ** (exponent - 15) * (1 + fraction / 1024);
}

function parseHeaderDict(header: string): {
  descr: string;
  fortranOrder: boolean;
  shape: number[];
} {
  const descrMatch = /'descr':\s*'([^']+)'/.exec(header);
  const fortranMatch = /'fortran_order':\s*(True|False)/.exec(header);
  const shapeMatch = /'shape':\s*\(([^)]*)\)/.exec(header);
  if (!descrMatch || !fortranMatch || !shapeMatch) {
    throw new NpyParseError(`could not parse .npy header dict: ${header}`);
  }
  const shapeText = shapeMatch[1]?.trim() ?? "";
  const shape =
    shapeText.length === 0
      ? []
      : shapeText
          .split(",")
          .map((part) => part.trim())
          .filter((part) => part.length > 0)
          .map((part) => {
            const value = Number.parseInt(part, 10);
            if (!Number.isFinite(value)) throw new NpyParseError(`bad shape entry: ${part}`);
            return value;
          });
  return { descr: descrMatch[1] ?? "", fortranOrder: fortranMatch[1] === "True", shape };
}

/** Parses a `.npy` file's bytes into a flat float64 array plus shape/dtype metadata. */
export function parseNpy(buffer: ArrayBuffer): NpyArray {
  const view = new DataView(buffer);
  for (let index = 0; index < MAGIC.length; index += 1) {
    if (view.getUint8(index) !== MAGIC[index]) {
      throw new NpyParseError("missing .npy magic bytes");
    }
  }
  const major = view.getUint8(6);
  let headerLenOffset: number;
  let headerLenSize: 2 | 4;
  if (major === 1) {
    headerLenOffset = 8;
    headerLenSize = 2;
  } else {
    headerLenOffset = 8;
    headerLenSize = 4;
  }
  const headerLen =
    headerLenSize === 2
      ? view.getUint16(headerLenOffset, true)
      : view.getUint32(headerLenOffset, true);
  const headerStart = headerLenOffset + headerLenSize;
  const headerBytes = new Uint8Array(buffer, headerStart, headerLen);
  const header = new TextDecoder("ascii").decode(headerBytes);
  const { descr, fortranOrder, shape } = parseHeaderDict(header);
  if (fortranOrder) {
    throw new NpyParseError("fortran-order .npy arrays are not supported");
  }
  const dataStart = headerStart + headerLen;
  const count = shape.reduce((product, dim) => product * dim, 1);
  const data = new Float64Array(count);
  const normalizedDescr = descr.replace(/^[<>|=]/, "");
  const littleEndian = !descr.startsWith(">");

  const readers: Record<string, (offset: number) => number> = {
    f2: (offset) => float16ToFloat64(view.getUint16(offset, littleEndian)),
    f4: (offset) => view.getFloat32(offset, littleEndian),
    f8: (offset) => view.getFloat64(offset, littleEndian),
    u1: (offset) => view.getUint8(offset),
    i1: (offset) => view.getInt8(offset),
    u2: (offset) => view.getUint16(offset, littleEndian),
    i2: (offset) => view.getInt16(offset, littleEndian),
    u4: (offset) => view.getUint32(offset, littleEndian),
    i4: (offset) => view.getInt32(offset, littleEndian),
    // Number(bigint) narrowing is exact up to 2**53 — see the module
    // docstring for why that's safe for every int64/uint64 this codebase
    // actually decodes (neuron/region/slot counts, never a huge quantity).
    u8: (offset) => Number(view.getBigUint64(offset, littleEndian)),
    i8: (offset) => Number(view.getBigInt64(offset, littleEndian)),
  };
  const itemSizes: Record<string, number> = {
    f2: 2,
    f4: 4,
    f8: 8,
    u1: 1,
    i1: 1,
    u2: 2,
    i2: 2,
    u4: 4,
    i4: 4,
    u8: 8,
    i8: 8,
  };
  const reader = readers[normalizedDescr];
  const itemSize = itemSizes[normalizedDescr];
  if (!reader || !itemSize) {
    throw new NpyParseError(`unsupported .npy dtype: ${descr}`);
  }
  for (let index = 0; index < count; index += 1) {
    data[index] = reader(dataStart + index * itemSize);
  }
  return { shape, descr, fortranOrder, data };
}
